import Vue from 'vue'
import VueRouter from 'vue-router'

Vue.use(VueRouter)

// Used meta attributes
//
// requiresAuth: false
// Route can be accessed without user log in
//
// layout: 'auth'
// Rendered using the very simple auth layout without navigation etc

// Roles:
//
// "company_admin", # manages their Company, their Company's Users, Projects, Locations and company settings
// "reporting_analyst", # has dashboard access
// "container_manager", # can manage Containers, Fractions, Sensors (could be an external person)
// "route_planner", # manages RouteTemplates, RouteEvents, Routes, Vehicles, ContainerGroups
// "route_driver", # can use the binity-tour app
// "order_manager", # can manage Orders, Sites, Areas, Zones, Vehicles in binity-app
// "order_creator", # can use the binity-order app
// "order_driver", # can use the binity-driver app

export const isAuthorized = (companyUser, route) => {
  if (route.meta.requiresAuth === false) return true // routes that don't require auth
  if (route.meta.roles === 'all') return true // routes that everyone can access
  if (companyUser == null) return false

  if (route.meta.industryOnly && companyUser.company_type !== 'industry') return false
  if (route.meta.branchOnly && companyUser.company_type !== 'branch') return false

  if (route.meta.companySetting) {
    if (companyUser.company) {
      if (companyUser.company.settings[route.meta.companySetting] !== true) return false
    } else {
      return false
    }
  }

  // the company specific roles and admin if the user is admin
  const roles = [...companyUser.roles, ...(companyUser.user.admin ? ['admin'] : [])]

  return roles.some(role => route.meta.roles?.includes(role)) // check the user has the required role for this company
}

export const getRedirectPath = (companyUser, path) => {
  if (companyUser == null) return '/'

  const roles = companyUser.roles
  // treat admins like company admins
  if (companyUser.user_admin) roles.push('company_admin')
  const hasRole = role => roles.includes(role)

  if (companyUser.company_type == 'branch') {
    // Branch Companies
    if (path.startsWith('/company')) {
      // In the /company Area
      if (hasRole('company_admin')) return '/company'
      if (hasRole('reporting_analyst')) return '/reporting'
      if (hasRole('container_manager')) return '/company/containers'
      if (hasRole('route_planner')) return '/company/routes'
    } else {
      // In the normal Area
      if (hasRole('company_admin')) return '/dashboard'
      if (hasRole('reporting_analyst')) return '/reporting'
      if (hasRole('container_manager')) return '/containers'
      if (hasRole('route_planner')) return '/routes'
    }
  } else {
    // Industry Companies
    if (path.startsWith('/company')) {
      // In the /company Area
      if (hasRole('company_admin')) return '/company'
      if (hasRole('reporting_analyst')) return '/reporting'
      if (hasRole('container_manager')) return '/company/containers'
      if (hasRole('order_manager')) return '/company/vehicles'
    } else {
      // In the normal Area
      if (hasRole('company_admin')) return '/orders'
      if (hasRole('reporting_analyst')) return '/reporting'
      if (hasRole('container_manager')) return '/containers'
      if (hasRole('order_manager')) return '/orders'
    }
  }

  return '/' // fallback e.g. for roles who don't have binity-app access
}

const routes = [
  {
    path: '/',
    name: 'home', // this is only used for redirection and displaying an error message if the user has no company assigned
    component: () => import('../views/home.vue'),
    meta: {
      roles: 'all',
    },
  },

  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/privacy.vue'),
    meta: {
      requiresAuth: false,
    },
  },

  //
  // Authentication
  //
  {
    path: '/auth/sign_in',
    name: 'auth_sign_in',
    component: () => import('../views/auth/sign_in.vue'),
    meta: {
      requiresAuth: false,
      layout: 'auth',
    },
  },

  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/dashboard.vue'),
    meta: { roles: ['admin', 'company_admin', 'reporting_analyst'], branchOnly: true },
  },

  {
    path: '/user',
    name: 'user_show',
    component: () => import('../views/user/show.vue'),
    meta: { roles: 'all' },
  },

  //
  // Companies
  //

  {
    path: '/company',
    name: 'company_details',
    component: () => import('../views/companies/show.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },

  {
    path: '/company/users/:user_uuid',
    name: 'company_user',
    component: () => import('../views/companies/users/show.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },

  {
    path: '/company/locations/new',
    name: 'new_company_location',
    component: () => import('../views/companies/locations/new.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/locations/:location_uuid',
    name: 'company_location',
    component: () => import('../views/companies/locations/show.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/locations/:location_uuid/dumping_stations/new',
    name: 'company_location_dumping_stations',
    component: () => import('../views/companies/locations/dumping_stations/new.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/projects',
    name: 'company_projects',
    component: () => import('../views/companies/projects/index.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/projects/new',
    name: 'new_company_project',
    component: () => import('../views/companies/projects/new.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/projects/:project_uuid',
    name: 'company_project',
    component: () => import('../views/companies/projects/show.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/containers',
    name: 'company_containers',
    component: () => import('../views/companies/containers/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/new',
    name: 'new_container',
    component: () => import('../views/companies/containers/ContainerNewForm.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/import',
    name: 'import_container',
    component: () => import('../views/companies/containers/import.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/:container_uuid',
    name: 'company_container',
    component: () => import('../views/companies/containers/ContainerShowForm.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager', 'route_planner'] },
  },
  {
    path: '/company/containers/:container_uuid/sensors/new',
    name: 'new_container_sensor',
    component: () => import('../views/companies/containers/sensors/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/:container_uuid/images/new',
    name: 'new_container_image',
    component: () => import('../views/companies/containers/images/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/:container_uuid/uvv_checks/new',
    name: 'new_container_uvv_check',
    component: () => import('../views/companies/containers/uvv_checks/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/:container_uuid/empty_templates/new',
    name: 'new_container_empty_template',
    component: () => import('../views/companies/containers/empty_templates/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/containers/:container_uuid/empty_templates/:template_uuid',
    name: 'container_empty_template',
    component: () => import('../views/companies/containers/empty_templates/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager', 'route_planner'] },
  },

  {
    path: '/company/containers/:container_uuid/identifiers/new',
    name: 'new_container_identifier',
    component: () => import('../views/companies/containers/identifiers/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },

  {
    path: '/company/containers/:container_uuid/identifiers/:identifier_uuid',
    name: 'container_identifier',
    component: () => import('../views/companies/containers/identifiers/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/container_groups',
    name: 'company_container_groups',
    component: () => import('../views/companies/container_groups/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'], branchOnly: true },
  },
  {
    path: '/company/container_groups/:container_group_uuid',
    name: 'company_container_group',
    component: () => import('../views/companies/container_groups/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'], branchOnly: true },
  },
  {
    path: '/company/container_groups/:container_group_uuid/empty_templates/new',
    name: 'newContainerGroupEmptyTemplate',
    component: () => import('../views/companies/container_groups/empty_templates/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'], branchOnly: true },
  },
  {
    path: '/company/container_groups/:container_group_uuid/empty_templates/:template_uuid',
    name: 'containerGroupEmptyTemplate',
    component: () => import('../views/companies/container_groups/empty_templates/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'], branchOnly: true },
  },

  {
    path: '/company/cost_centers',
    name: 'cost_centers',
    component: () => import('../views/companies/cost_centers/index.vue'),
    meta: { roles: ['admin', 'company_admin'], industryOnly: true },
  },
  {
    path: '/company/cost_centers/new',
    name: 'new_cost_center',
    component: () => import('../views/companies/cost_centers/new.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },
  {
    path: '/company/cost_centers/:cost_center_uuid',
    name: 'cost_center',
    component: () => import('../views/companies/cost_centers/show.vue'),
    meta: { roles: ['admin', 'company_admin'] },
  },

  {
    path: '/company/sensors',
    name: 'company_sensors',
    component: () => import('../views/companies/sensors/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/sensors/new',
    name: 'new_sensor',
    component: () => import('../views/companies/sensors/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/sensors/import',
    name: 'import_sensor',
    component: () => import('../views/companies/sensors/import.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/sensors/:sensor_uuid',
    name: 'sensor',
    component: () => import('../views/companies/sensors/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/vehicles',
    name: 'company_vehicles',
    component: () => import('../views/companies/vehicles/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager', 'route_planner'] },
  },
  {
    path: '/company/vehicles/new',
    name: 'new_vehicle',
    component: () => import('../views/companies/vehicles/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager', 'route_planner'] },
  },
  {
    path: '/company/vehicles/:vehicle_uuid',
    name: 'company_vehicle',
    component: () => import('../views/companies/vehicles/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager', 'route_planner'] },
  },

  {
    path: '/company/vehicles/:vehicle_uuid/chambers/new',
    name: 'new_vehicle_chamber',
    component: () => import('../views/companies/vehicles/chambers/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager', 'route_planner'] },
  },

  {
    path: '/company/vehicles/:vehicle_uuid/chambers/:chamber_uuid',
    name: 'vehicle_chamber',
    component: () => import('../views/companies/vehicles/chambers/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager', 'route_planner'] },
  },
  {
    path: '/company/routes',
    name: 'company_routes',
    component: () => import('../views/companies/routes/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },
  {
    path: '/company/routes/:route_uuid',
    name: 'company_route',
    component: () => import('../views/companies/routes/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },

  {
    path: '/company/route_editor',
    name: 'company_route_editor',
    component: () => import('../views/companies/routes/route_editor/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },

  {
    path: '/company/route_templates/new',
    name: 'route_templates',
    component: () => import('../views/companies/route_templates/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },
  {
    path: '/company/route_templates/:template_uuid',
    name: 'route_template',
    component: () => import('../views/companies/route_templates/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },

  {
    path: '/company/route_events/:event_uuid',
    name: 'route_event',
    component: () => import('../views/companies/route_events/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },

  {
    path: '/company/sites',
    name: 'company_sites',
    component: () => import('../views/companies/sites/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/new',
    name: 'new_site',
    component: () => import('../views/companies/sites/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/:site_uuid',
    name: 'site',
    component: () => import('../views/companies/sites/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/:site_uuid/areas/new',
    name: 'new_area',
    component: () => import('../views/companies/sites/areas/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/:site_uuid/areas/:area_uuid',
    name: 'area',
    component: () => import('../views/companies/sites/areas/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/:site_uuid/areas/:area_uuid/zones/new',
    name: 'new_zone',
    component: () => import('../views/companies/sites/areas/zones/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/:site_uuid/areas/:area_uuid/zones/:zone_uuid',
    name: 'zone',
    component: () => import('../views/companies/sites/areas/zones/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/sites/:site_uuid/areas/:area_uuid/zones/:zone_uuid/identifiers/new',
    name: 'new_zone_identifier',
    component: () => import('../views/companies/sites/areas/zones/identifier/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/company/weigh_stations',
    name: 'weigh_stations',
    component: () => import('../views/companies/weigh_stations/index.vue'),
    meta: { roles: ['admin', 'company_admin'], industryOnly: true },
  },
  {
    path: '/company/weigh_stations/new',
    name: 'new_weigh_station',
    component: () => import('../views/companies/weigh_stations/new.vue'),
    meta: { roles: ['admin', 'company_admin'], industryOnly: true },
  },
  {
    path: '/company/weigh_stations/:weigh_station_uuid',
    name: 'weigh_station',
    component: () => import('../views/companies/weigh_stations/show.vue'),
    meta: { roles: ['admin', 'company_admin'], industryOnly: true },
  },
  {
    path: '/company/fractions',
    name: 'fractions',
    component: () => import('../views/companies/fractions/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/fractions/new',
    name: 'newFraction',
    component: () => import('../components/Fractions/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },
  {
    path: '/company/fractions/:fractionUuid',
    name: 'showFraction',
    component: () => import('../components/Fractions/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager'] },
  },

  //
  // Notifications
  //
  {
    path: '/notifications',
    name: 'all_notifications',
    component: () => import('../views/notifications/index.vue'),
    meta: { roles: 'all' },
  },

  {
    path: '/notifications/channels/new',
    name: 'new_notification_channel',
    component: () => import('../views/notifications/channels/new.vue'),
    meta: { roles: 'all' },
  },
  {
    path: '/notifications/channels/:uuid',
    name: 'show_notification_channel',
    component: () => import('../views/notifications/channels/show.vue'),
    meta: { roles: 'all' },
  },
  {
    path: '/notifications/topics/new',
    name: 'new_notification_topic',
    component: () => import('../views/notifications/topics/new.vue'),
    meta: { roles: 'all' },
  },
  {
    path: '/notifications/topics/:uuid',
    name: 'show_notification_topic',
    component: () => import('../views/notifications/topics/show.vue'),
    meta: { roles: 'all' },
  },

  //
  // Containers
  //

  {
    path: '/containers',
    name: 'containers',
    component: () => import('../views/containers/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager', 'reporting_analyst', 'route_planner'] },
  },
  {
    path: '/containers/:uuid',
    name: 'container_details',
    component: () => import('../views/containers/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager', 'reporting_analyst', 'route_planner'] },
  },

  //
  // Orders
  //

  {
    path: '/orders/new',
    name: 'new_order',
    component: () => import('../views/orders/new.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },
  {
    path: '/orders/:orderUuid?', // index and show in one
    name: 'orders',
    component: () => import('../views/orders/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'order_manager'], industryOnly: true },
  },

  //
  // Order Templates
  //

  {
    path: '/order_templates',
    name: 'order_templates',
    component: () => import('../views/order_templates/index.vue'),
    meta: {
      roles: ['admin', 'company_admin', 'order_manager'],
      companySetting: 'enable_order_templates',
      industryOnly: true,
    },
  },
  {
    path: '/order_templates/new',
    name: 'new_order_template',
    component: () => import('../views/order_templates/new.vue'),
    meta: {
      roles: ['admin', 'company_admin', 'order_manager'],
      companySetting: 'enable_order_templates',
      industryOnly: true,
    },
  },
  {
    path: '/order_templates/:orderTemplateUuid',
    name: 'show_order_template',
    component: () => import('../views/order_templates/show.vue'),
    meta: {
      roles: ['admin', 'company_admin', 'order_manager'],
      companySetting: 'enable_order_templates',
      industryOnly: true,
    },
  },

  //
  // Container Groups
  //

  {
    path: '/container_groups',
    name: 'container_groups',
    component: () => import('../views/container_groups/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager', 'route_planner'], branchOnly: true },
  },
  {
    path: '/container_groups/:uuid',
    name: 'container_group',
    component: () => import('../views/container_groups/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'container_manager', 'route_planner'], branchOnly: true },
  },

  //
  // Routes
  //

  {
    path: '/routes',
    name: 'all_routes',
    component: () => import('../views/routes/index.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },
  {
    path: '/routes/:uuid',
    name: 'route',
    component: () => import('../views/routes/show.vue'),
    meta: { roles: ['admin', 'company_admin', 'route_planner'], branchOnly: true },
  },

  //
  // Reporting Dashboard
  //

  {
    path: '/reporting',
    name: 'reporting',
    component: () => import('../views/reporting.vue'),
    meta: { roles: ['admin', 'company_admin', 'reporting_analyst'] },
  },

  //
  // ADMIN AREA
  //

  {
    path: '/admin/users',
    name: 'admin_users',
    component: () => import('../views/admin/users/index.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/users/new',
    name: 'new_admin_user',
    component: () => import('../views/admin/users/new.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/users/:uuid',
    name: 'admin_user',
    component: () => import('../views/admin/users/show.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/users/:uuid/companies/new',
    name: 'new_admin_user_company',
    component: () => import('../views/admin/users/companies/new.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/users/:uuid/companies/:company_uuid',
    name: 'admin_user_company',
    component: () => import('../views/admin/users/companies/show.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/companies',
    name: 'admin_companies',
    component: () => import('../views/admin/companies/index.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/companies/new',
    name: 'new_admin_company',
    component: () => import('../views/admin/companies/new.vue'),
    meta: { roles: ['admin'] },
  },
  {
    path: '/admin/companies/:uuid',
    name: 'admin_company',
    component: () => import('../views/admin/companies/show.vue'),
    meta: { roles: ['admin'] },
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

export default router
